import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ForbiddenProps {
  /** Optional page name, so the message can say what was denied. */
  page?: string;
}

/**
 * 403 — signed in, but this group's permission row denies the page.
 *
 * Distinct from <NotAuthorized /> (401) on purpose. 401 means "we do not know who you
 * are" and is fixed by signing in; 403 means "we know exactly who you are and the answer
 * is no", which signing in again cannot change. Offering a login button here would send
 * the user round a loop that always lands back on this screen — so the only way out is
 * back into the app, or an administrator granting the page in the permissions matrix.
 */
const Forbidden: React.FC<ForbiddenProps> = ({ page }) => {
  const { t } = useTranslation();

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center"
      style={{ minHeight: '70vh', padding: '2rem' }}
    >
      <div style={{ fontSize: 64, fontWeight: 800, color: '#b06a1f', lineHeight: 1 }}>403</div>
      <h4 style={{ color: '#101413', margin: '1rem 0 .5rem' }}>
        {t('errors.forbidden_title') || 'You do not have access to this page'}
      </h4>
      <p className="text-muted" style={{ maxWidth: 560, marginBottom: '1.75rem' }}>
        {t('errors.forbidden_body') ||
          'Your account is signed in, but your group does not have permission for this screen. Contact an administrator if you need access.'}
        {page ? ` (${page})` : ''}
      </p>
      <Link
        to="/"
        className="btn"
        style={{ background: '#1f7c50', color: '#fff', fontWeight: 600, padding: '.6rem 1.5rem', borderRadius: 8 }}
      >
        {t('errors.back_home') || 'Back to home'}
      </Link>
    </div>
  );
};

export default Forbidden;
